import type { Box } from "./building.ts";
import type { Cylinder } from "./movement.ts";
import { MOVE } from "./movement-constants.ts";
import { STRUCTURE_COLLIDER } from "./structure-collider.ts";

/** Regras de posicionamento de estações (cliente prevê, servidor decide com a mesma função). */
export const PLACEMENT = {
  /** Alcance horizontal a partir do corpo de quem posiciona. */
  reach: 4,
  /** tan(20°): só chão quase plano. */
  maxSlope: 0.36397023426620234,
  /** Abaixo disso é água (o mar fica em 0). */
  minHeight: 0.2,
  /** Folga entre o colisor novo e os que já existem. */
  clearance: 0.1,
} as const;

export interface PlacementWorld {
  height(x: number, z: number): number;
  gradient(x: number, z: number, out: { x: number; z: number }): { x: number; z: number };
  cylinders(x: number, z: number, radius: number, out: Cylinder[]): Cylinder[];
  /** Peças de construção (F17): a estação assenta no piso e não entra numa parede. */
  boxes?(x: number, z: number, radius: number, out: Box[]): Box[];
}

export type PlacementBlocker =
  | "longe demais"
  | "não dá para posicionar na água"
  | "chão inclinado demais"
  | "tem algo no caminho"
  | "tem alguém no caminho"
  | "tem uma construção no caminho";

/**
 * Onde a estação assenta em (x, z): o terreno, ou o topo de um piso ou alicerce sob o ponto a até
 * um degrau acima dele (dentro de casa, sobre o assoalho). Rampa não serve de base.
 */
export function placementBase(world: PlacementWorld, x: number, z: number): { y: number; onBox: boolean } {
  const terrain = world.height(x, z);
  let y = terrain;
  let onBox = false;
  if (!world.boxes) return { y, onBox };
  for (const b of world.boxes(x, z, 0.1, [])) {
    if (b.ramp || x < b.minX || x > b.maxX || z < b.minZ || z > b.maxZ) continue;
    const top = b.maxY;
    if (top > terrain + MOVE.stepHeight || top <= y) continue;
    y = top;
    onBox = true;
  }
  return { y, onBox };
}

const sq = (v: number) => v * v;

/**
 * Motivo para não posicionar em (x, z), ou `undefined` se pode. `from` é quem posiciona;
 * `bodies`, os corpos dos jogadores (inclusive o de quem posiciona), para ninguém ficar preso.
 */
export function placementBlocker(
  world: PlacementWorld,
  from: { x: number; z: number },
  x: number,
  z: number,
  bodies: Iterable<{ x: number; z: number }> = [],
): PlacementBlocker | undefined {
  if (sq(x - from.x) + sq(z - from.z) > sq(PLACEMENT.reach)) return "longe demais";
  const base = placementBase(world, x, z);
  // Sobre um piso, a água e a encosta embaixo não importam.
  if (!base.onBox && world.height(x, z) < PLACEMENT.minHeight) return "não dá para posicionar na água";
  const g = world.gradient(x, z, { x: 0, z: 0 });
  if (!base.onBox && sq(g.x) + sq(g.z) > sq(PLACEMENT.maxSlope)) return "chão inclinado demais";
  const radius = STRUCTURE_COLLIDER.radius;
  for (const c of world.cylinders(x, z, radius, [])) {
    if (sq(c.x - x) + sq(c.z - z) < sq(c.radius + radius + PLACEMENT.clearance)) return "tem algo no caminho";
  }
  for (const b of bodies) if (sq(b.x - x) + sq(b.z - z) < sq(MOVE.radius + radius)) return "tem alguém no caminho";
  // Peças que entram no volume da estação (paredes, a laje de cima); a que a apoia não conta.
  const reach = radius + PLACEMENT.clearance;
  for (const b of world.boxes?.(x, z, reach + 1, []) ?? []) {
    if (b.maxY <= base.y + 0.01 || b.minY >= base.y + STRUCTURE_COLLIDER.height) continue;
    const cx = x < b.minX ? b.minX : x > b.maxX ? b.maxX : x;
    const cz = z < b.minZ ? b.minZ : z > b.maxZ ? b.maxZ : z;
    if (sq(x - cx) + sq(z - cz) < sq(reach)) return "tem uma construção no caminho";
  }
  return undefined;
}
